(() => {
  const fields = ['ideal', 'height', 'capacity', 'speed', 'warranty', 'addons', 'colours', 'overview'];
  const extraFields = new Set(['speed', 'warranty', 'addons', 'colours', 'overview']);
  const labels = {
    ideal: 'Ideal for', height: 'Height range', capacity: 'Weight capacity',
    speed: 'Speed', warranty: 'Warranty', addons: 'Recommended add-ons',
    colours: 'Colour options', overview: 'Overview',
  };
  const colourValues = {
    Black: '#08090a', White: '#fff', 'Dark brown': '#6b554f',
    'Light brown': '#d7bc9b', Grey: '#b8bcba',
  };

  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }

  function safeUrl(raw) {
    try {
      const url = new URL(raw, window.location.href);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  function sizedImage(url, width) {
    const imageUrl = new URL(url);
    imageUrl.searchParams.set('width', String(width));
    return imageUrl.href;
  }

  function productCard(desk, index, titleTag) {
    const card = node('article', 'desk-card');
    const link = node('a', 'desk-card__image');
    link.href = desk.url;
    link.setAttribute('aria-label', `View ${desk.name}${desk.badge ? `, ${desk.badge}` : ''}`);

    if (desk.image) {
      const image = node('img');
      image.src = sizedImage(desk.image, 640);
      image.srcset = `${sizedImage(desk.image, 320)} 320w, ${sizedImage(desk.image, 640)} 640w`;
      image.sizes = '(max-width: 800px) 280px, 258px';
      image.alt = desk.name;
      image.width = 350;
      image.height = 350;
      image.loading = titleTag === 'h3' || index >= 5 ? 'lazy' : 'eager';
      image.decoding = 'async';
      link.append(image);
    } else {
      link.append(node('span', 'desk-card__image-placeholder', desk.name));
    }

    if (desk.badge) link.append(node('span', 'desk-card__badge', desk.badge));
    const title = node(titleTag, 'desk-card__title');
    const titleLink = node('a', '', desk.name);
    titleLink.href = desk.url;
    title.append(titleLink);
    card.append(link, title);
    return card;
  }

  function specValue(desk, field) {
    return field === 'overview' ? desk.description : desk[field];
  }

  function isLinkedValue(value) {
    return value === 'See product page' || value === 'See warranty policy';
  }

  function specLink(desk, field, value) {
    const link = node('a', 'value-cell__link', value);
    link.href = value === 'See warranty policy'
      ? 'https://motiongrey.com/pages/warranty' : desk.url;
    link.setAttribute('aria-label', value === 'See warranty policy'
      ? `See warranty policy for ${desk.name}`
      : `See ${desk.name} product page for ${labels[field]}`);
    return link;
  }

  function valueCell(desk, field) {
    const cell = node('div', `value-cell value-cell--${field}`);
    if (field === 'colours') {
      const swatches = node('div', 'swatches');
      for (const colour of desk.colours) {
        const item = node('span', 'swatch-item');
        const swatch = node('span', 'swatch');
        swatch.style.setProperty('--swatch', colourValues[colour] || '#aeb3b4');
        item.title = colour;
        item.append(swatch, node('span', 'swatch-name', colour));
        swatches.append(item);
      }
      cell.append(swatches);
    } else {
      const value = specValue(desk, field);
      if (isLinkedValue(value)) cell.append(specLink(desk, field, value));
      else cell.textContent = value || '—';
    }
    return cell;
  }

  class MgDeskLineup extends HTMLElement {
    connectedCallback() {
      if (this._initialized) {
        this._resizeObserver?.observe(this._productStrip);
        this._headers?.forEach((header) => {
          this._headerResizeObserver?.observe(header);
          this._headerMutationObserver?.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
        });
        window.addEventListener('scroll', this._updateStickyOffset, { passive: true });
        this._updateStickyOffset();
        return;
      }
      const root = this.querySelector('[data-comparison]');
      const data = this.querySelector('[data-products]');
      if (!root || !data) return;

      let desks;
      try {
        desks = JSON.parse(data.textContent || '[]');
      } catch {
        return;
      }
      if (!Array.isArray(desks)) return;
      desks = desks.map((desk) => ({
        ...desk,
        name: String(desk.name || '').trim(),
        badge: String(desk.badge || '').trim(),
        description: String(desk.description || '').trim(),
        url: safeUrl(desk.url),
        image: safeUrl(desk.image),
        colours: (Array.isArray(desk.colours) ? desk.colours : [])
          .map((colour) => String(colour).trim()).filter(Boolean),
      })).filter((desk) => desk.name && desk.url);
      if (!desks.length) return;

      const strips = [...root.querySelectorAll('[data-strip]')];
      const productStrip = root.querySelector('[data-strip="products"]');
      const previous = root.querySelector('[data-direction="prev"]');
      const next = root.querySelector('[data-direction="next"]');
      const currentDesk = root.querySelector('[data-current-desk]');
      const currentPosition = root.querySelector('[data-current-position]');
      const moreSpecs = root.querySelector('[data-more-specs]');
      const moreToggle = root.querySelector('[data-more-toggle]');
      const moreLabel = root.querySelector('[data-more-label]');
      const tracks = Object.fromEntries([...root.querySelectorAll('[data-track]')]
        .map((track) => [track.dataset.track, track]));
      const titleTag = root.querySelector('.comparison__topbar h2') ? 'h3' : 'h2';

      for (const [index, desk] of desks.entries()) {
        tracks.products.append(productCard(desk, index, titleTag));
        for (const field of fields) tracks[field].append(valueCell(desk, field));
      }
      for (const field of fields) {
        if (desks.some((desk) => isLinkedValue(specValue(desk, field)))) {
          root.querySelector(`[data-strip="${field}"]`)?.closest('.spec')?.removeAttribute('aria-hidden');
        }
      }

      const table = root.querySelector('[data-accessible-table]');
      const header = table.tHead.insertRow();
      for (const label of ['Specification', ...desks.map((desk) => desk.name)]) {
        const heading = node('th', '', label);
        heading.scope = 'col';
        header.append(heading);
      }
      const extraRows = [];
      for (const field of fields) {
        const row = table.tBodies[0].insertRow();
        if (extraFields.has(field)) extraRows.push(row);
        const heading = node('th', '', labels[field]);
        heading.scope = 'row';
        row.append(heading);
        for (const desk of desks) {
          const value = field === 'colours' ? desk.colours.join(', ') : specValue(desk, field);
          row.append(node('td', '', value || '—'));
        }
      }
      if (moreSpecs.hidden) extraRows.forEach((row) => { row.hidden = true; });

      let syncing = false;
      const updateButtons = () => {
        const max = productStrip.scrollWidth - productStrip.clientWidth;
        previous.disabled = productStrip.scrollLeft <= 2;
        next.disabled = productStrip.scrollLeft >= max - 2;
        const step = tracks.products.firstElementChild.getBoundingClientRect().width;
        const index = Math.min(desks.length - 1, Math.max(0, Math.round(productStrip.scrollLeft / step)));
        if (currentDesk.textContent !== desks[index].name) currentDesk.textContent = desks[index].name;
        currentPosition.textContent = `${index + 1} of ${desks.length}`;
      };
      const sync = (source) => {
        if (syncing) return;
        syncing = true;
        for (const strip of strips) if (strip !== source) strip.scrollLeft = source.scrollLeft;
        updateButtons();
        requestAnimationFrame(() => { syncing = false; });
      };

      moreToggle.addEventListener('click', () => {
        const expanded = moreToggle.getAttribute('aria-expanded') !== 'true';
        syncing = true;
        moreSpecs.hidden = !expanded;
        for (const row of extraRows) row.hidden = !expanded;
        moreToggle.setAttribute('aria-expanded', String(expanded));
        moreLabel.textContent = expanded ? 'See less' : 'See more';
        requestAnimationFrame(() => {
          for (const strip of strips) if (strip !== productStrip) strip.scrollLeft = productStrip.scrollLeft;
          updateButtons();
          requestAnimationFrame(() => { syncing = false; });
        });
      });
      for (const strip of strips) strip.addEventListener('scroll', () => sync(strip), { passive: true });
      for (const button of [previous, next]) {
        button.addEventListener('click', () => {
          const step = tracks.products.firstElementChild.getBoundingClientRect().width;
          const direction = button.dataset.direction === 'next' ? 1 : -1;
          const index = Math.round(productStrip.scrollLeft / step) + direction;
          const max = productStrip.scrollWidth - productStrip.clientWidth;
          productStrip.scrollLeft = Math.min(max, Math.max(0, index * step));
        });
      }

      this.querySelector('[data-fallback]').hidden = true;
      this._initialized = true;
      this._productStrip = productStrip;
      this._resizeObserver = new ResizeObserver(updateButtons);
      this._resizeObserver.observe(productStrip);
      this._headers = [...document.querySelectorAll('[data-sticky-pc], [data-sticky-mb]')];
      this._updateStickyOffset = () => {
        const bottom = this._headers.reduce((offset, header) => {
          const style = getComputedStyle(header);
          const rect = header.getBoundingClientRect();
          const pinned = style.position === 'fixed' || style.position === 'sticky';
          return pinned && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0 && rect.top <= 1
            ? Math.max(offset, rect.bottom) : offset;
        }, 0);
        this.style.setProperty('--mg-dl-sticky-top', `${Math.max(0, Math.ceil(bottom))}px`);
      };
      this._headerResizeObserver = new ResizeObserver(this._updateStickyOffset);
      this._headerMutationObserver = new MutationObserver(this._updateStickyOffset);
      this._headers.forEach((header) => {
        this._headerResizeObserver.observe(header);
        this._headerMutationObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
      });
      window.addEventListener('scroll', this._updateStickyOffset, { passive: true });
      this._updateStickyOffset();
      updateButtons();
    }

    disconnectedCallback() {
      this._resizeObserver?.disconnect();
      this._headerResizeObserver?.disconnect();
      this._headerMutationObserver?.disconnect();
      if (this._updateStickyOffset) window.removeEventListener('scroll', this._updateStickyOffset);
    }
  }

  if (!customElements.get('mg-desk-lineup')) customElements.define('mg-desk-lineup', MgDeskLineup);
})();
